import React, { Suspense, lazy, useEffect, useMemo, useRef, useState } from 'react'
import { useNodesState } from 'reactflow'
import Dialog from '@mui/material/Dialog'
import DialogContent from '@mui/material/DialogContent'
import Box from '@mui/material/Box'
import Stack from '@mui/material/Stack'
import Button from '@mui/material/Button'
import Typography from '@mui/material/Typography'
import CircularProgress from '@mui/material/CircularProgress'
import IconButton from '@mui/material/IconButton'
import { alpha, useTheme } from '@mui/material/styles'
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined'
import BuildOutlinedIcon from '@mui/icons-material/BuildOutlined'
import ArticleOutlinedIcon from '@mui/icons-material/ArticleOutlined'
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined'
import InsightsOutlinedIcon from '@mui/icons-material/InsightsOutlined'
import CategoryOutlinedIcon from '@mui/icons-material/CategoryOutlined'
import WarningAmberRounded from '@mui/icons-material/WarningAmberRounded'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import HourglassTopOutlined from '@mui/icons-material/HourglassTopOutlined'
import CloseRounded from '@mui/icons-material/CloseRounded'
import { SmartDialog } from '../../../generic/utilities/SmartDialog'
import createDialogTitle from '../../../generic/utilities/dialogUtil'
import { useSmartTranslation } from '../../../generic/hooks/useSmartTranslation'
import { useAuthorization } from '../../../generic/hooks/useAuthorization'
import useFrontEndConfig from '../../../generic/hooks/useFrontEndConfig/useFrontEndConfig'
import { apiGet, apiPut } from '../../../generic/Api_Functions'
import AlertUI from '../../../generic/AlertUI'
import DetailHeader from './DetailHeader'
import DetailErrorBoundary from './DetailErrorBoundary'
import GeneralBlock from './blocks/GeneralBlock'
import OperationsBlock from './blocks/OperationsBlock'
import DocumentsBlock from './blocks/DocumentsBlock'
import MaterialsBlock from './blocks/MaterialsBlock'
import ProgressBlock from './blocks/ProgressBlock'
import PartsBlock from './blocks/PartsBlock'
import UnsavedChangesPrompt from './UnsavedChangesPrompt'
import { changedFieldLabels } from './changedFieldLabels'
import ModifyQuantityDialog from '../ModifyQuantityDialog'
import { WorkflowHierarchyProvider } from '../../workflow2.0/WorkflowRecursionDetector'
import { isFabricationOrderOperable } from '../../../generic/utilities/statusPermissionGating'
import { describeWorkflow } from '../../../generic/utilities/workflowOrigin'
import { diffObjects } from '../../../generic/utilities/ObjectDiffUtil/objectDiffUtil'
import { findNewExternalJobMissingReturnTiming, updatePositions } from '../../workflow2.0/utils'
import { clearTempDocumentScopes, collectTempDocumentUUIDs } from '../../workflow2.0/tempDocumentScopes'
import { isDuplicatePlanningGroupSaveError, extractDuplicatePlanningGroupValues, DUPLICATE_PLANNING_GROUP_MESSAGE_KEY } from '../../workflow2.0/planningGroupSaveError'
import { alertForSaveError } from '../../workflow2.0/externalReturnSaveError'
import { prepareFabricationOrderWorkflowForSave } from '../../workflow2.0/feature/featureGroupRevisionMode'
import { withExplicitNightJob } from '../../workflow2.0/jobNightCharacter'
import SensitiveBoundary from '../../../vision/SensitiveBoundary'
import { useVision } from '../../../vision/visionContext'
import useDetailBlock from './useDetailBlock'
import useOperationExpansions from './useOperationExpansions'
import { useUnsavedChanges } from './useUnsavedChanges'
import {
  fetchHeader, fetchSheet, fetchAlerts, fetchOperations, fetchProgress, fetchDocuments,
  fetchChildren, fetchMaterialRequirements, fetchConsumedMaterial
} from './detailApi'
import { groundColor, raisedColor } from './detailSurfaces'
import { detailBlocksToReload } from './workflowAssignment'

// Las DOS pantallas que toman el sitio del detalle llegan a `three` por su cuenta
// (Workflow -> JobTask -> Document -> ... -> SmartThreejs; Balanced -> BalancedForm ->
// BalancedFormJobInfo -> TaskInstruction -> ...), y ninguna de las dos se monta hasta que el
// usuario la pide desde la cabecera. Importadas arriba, abrir la ficha por CUALQUIER bloque
// pagaba las dos, motor 3D incluido. `lazy` las deja donde ya estaban en la pantalla: detrás de
// su `viewMode`. `WorkflowHierarchyProvider` se queda estático a propósito -- es el contexto que
// envuelve, no llega a `three`, y sacarlo del camino no ahorra nada.
const Workflow = lazy(() => import('../../workflow2.0/Workflow'))
const Balanced = lazy(() => import('../../balanced/Balanced'))

// Las dos pantallas ocupan el cuerpo del diálogo entero: el hueco mientras llega el módulo se
// centra en él, en vez de saltar el contenido cuando aparece.
const TakeoverFallback = () => (
  <Stack alignItems='center' justifyContent='center' sx={{ flex: 1, minHeight: 240 }}>
    <CircularProgress size={32} />
  </Stack>
)

// Container + header + block navigation + per-block state machine (P-1, plan-jupiter.md §1.2).
// T1a built the skeleton (opening, identity, situation, frequent actions, lazy/cached block
// navigation). T1b wires the real mutations behind the header's actions (status/pause/resume/
// modify quantity), the 3 reference screens and the 404 ("order deleted") path.
// `key` is the internal identity (state, scenarios, tests); `labelKey` is what the user reads.
// Decisión del PO 2026-09-14: el bloque se llama "Workflow" en los 8 idiomas. La clave interna
// sigue siendo 'Operations' — renombrarla arrastraría los escenarios S129-S147 sin ganar nada.
const NAV_ITEMS = [
  { key: 'General', permission: '' },
  { key: 'Operations', labelKey: 'Workflow', permission: '' },
  { key: 'Documents', permission: 'DOCUMENTS_FABRICATION_ORDER' },
  { key: 'Materials', permission: '' },
  { key: 'Progress', permission: 'VIEW_PARTS_FABRICATION_ORDERS' },
  { key: 'Parts', permission: 'VIEW_PARTS_FABRICATION_ORDERS' }
]

// ⚠️ ADDENDUM 15 (E21) — MATERIALES ESTÁ OCULTO, NO RETIRADO. Decisión del PO del 2026-09-15: quiere
// valorar más adelante cómo completar el bloque y dejarlo bien, así que su código, sus componentes y
// sus tests SE QUEDAN y siguen en verde; lo que desaparece es su entrada en la navegación y, con
// ella, el acceso. Esto NO es un defecto ni un olvido: está apagado a propósito.
//
// PARA REACTIVARLO: vaciar esta lista. Es el único interruptor, y gobierna las dos consecuencias —la
// entrada de navegación y la llamada a N2— para que no se puedan reactivar por separado.
//
// ⚠️ Y por eso DEJA DE PEDIRSE N2: los avisos por bloque existen SOLO para alimentar la etiqueta de
// Materiales (RN7/RN52, único aviso definido en v1), así que con el bloque oculto esa llamada se
// pagaría en cada apertura para alimentar un distintivo que nadie ve. Mientras dure, el detalle no
// tiene NINGÚN aviso por bloque: RN7 queda sin manifestación visible, no derogada. Sun no cambia
// nada — N2, N7, N8 y N9 siguen servidos y sin consumidor, enteros para cuando el bloque vuelva.
//
// ⚠️ NO es un ajuste de usuario y no va a la pantalla de Ajustes: es una decisión de producto
// temporal. No confundir con `USE_FABRICATION_ORDER_DETAIL` (addendum 14), que sí es del cliente.
const HIDDEN_BLOCK_KEYS = ['Materials']

const isBlockHidden = (key) => HIDDEN_BLOCK_KEYS.includes(key)

// Pasada 17 (armazón visual, §6.1): one icon per block, purely decorative — never read by any test
// (nav items are found by role/accessible name, not by icon).
const NAV_ICON_BY_KEY = {
  General: DescriptionOutlinedIcon,
  Operations: BuildOutlinedIcon,
  Documents: ArticleOutlinedIcon,
  Materials: Inventory2OutlinedIcon,
  Progress: InsightsOutlinedIcon,
  Parts: CategoryOutlinedIcon
}

// Below the 'lg' breakpoint (1200px, §6.1) the nav column collapses to a 72px icon rail: the label
// stays in the accessible tree (screen readers/testing-library queries still resolve it) but is
// visually clipped, the standard "visually hidden" technique — never `display:none`, which would
// remove it from the accessibility tree too.
const navLabelSx = {
  '@media (max-width:1199.95px)': {
    // Mismo error que el hub encontró en PartsBlock: en el `sx` de MUI `width: 1` es la fracción
    // 1 = 100% y `margin: -1` son -8px de spacing, no un píxel. Unidades explícitas.
    position: 'absolute',
    width: '1px',
    height: '1px',
    overflow: 'hidden',
    clip: 'rect(0 0 0 0)',
    clipPath: 'inset(50%)',
    whiteSpace: 'nowrap',
    border: 0,
    padding: 0,
    margin: '-1px'
  }
}

const noopFetcher = () => Promise.resolve({})

const FabricationOrderDetailDialog = ({
  fabricationOrderId,
  open,
  onClose = () => {},
  initialBlock = 'General',
  // Story 213 v2 (S189): what the listing row said; the header says it too once it arrives (C1c)
  sensitive = false,
  // Told after anything that changes the order (General save, status, pause, quantity, workflow,
  // Balanced, 404), so the FO listing can reload its rows. Story 183 (S29): the "noche" column is
  // derived by Sun on a workflow save (RN10).
  onOrderChanged = () => {}
}) => {
  const { t_ } = useSmartTranslation()
  const isUserAuthorized = useAuthorization()
  const theme = useTheme()
  const [AlertElement, showAlert] = AlertUI()
  // #3060: handed to the workflow editor exactly as the legacy FO window does; without it the subworkflow
  // ACCEPT validates against `undefined` (validateWorkflow reads frontendConfig.workflowNodeSerialLinks) and
  // silently aborts.
  const [frontendConfig] = useFrontEndConfig()
  const [currentId, setCurrentId] = useState(fabricationOrderId)
  // Un bloque oculto no puede ser el bloque ACTIVO aunque el llamante lo pida por `initialBlock`:
  // sin esto, "oculto" significaba solo "sin entrada en la navegación" y el bloque seguía pintándose
  // — una puerta que el addendum 15 da por cerrada ("desaparece su entrada en la navegación y, con
  // ella, el acceso"). Hoy ningún llamante la usa, pero una puerta que el contrato cree cerrada es
  // peor que una que se sabe abierta. Se cae a General, que nunca está oculto.
  const [activeBlock, setActiveBlock] = useState(isBlockHidden(initialBlock) ? 'General' : initialBlock)
  const [modifyQuantityOpen, setModifyQuantityOpen] = useState(false)
  const [notFound, setNotFound] = useState(false)
  // Revisión del PO (pasada 24): "cambias algo y no sabes si ya está guardado". La confirmación
  // nació AQUÍ porque guardar invalidaba la cabecera y GeneralBlock se desmontaba mientras N1 se
  // releía. Desde que guardar relee en segundo plano (`refresh`) el bloque ya no se desmonta, pero
  // la barra se queda en el diálogo: es el pie común de la ventana y así sobrevive también a que el
  // usuario cambie de bloque en esos segundos.
  const [justSavedGeneral, setJustSavedGeneral] = useState(false)
  // Quién puede EDITAR el workflow no es quién puede VER la pestaña de procesos. Diagnóstico de la
  // pasada 26: T7 portó aquí la ventana de workflow del listado pero cambió la condición por
  // `isUserAuthorized('WORKFLOWS_TAB')`, que es el permiso de pestaña. Las dos pantallas que sí
  // editan preguntan por los índices disponibles del usuario: procesos exige al menos uno
  // (`MainWorkflows.jsx:28-33`) y la ventana original del listado exigía APPROVED
  // (`FabricationOrder.jsx:211-215` antes de `d006bd395`). Se replica ESA, que es la que esta acción
  // sustituye. Hoy `indexValues/available` devuelve DRAFT/PROPOSAL/APPROVED para smartpm, así que
  // ambas dan verdadero: el cambio no relaja nada, corrige a cuál se pregunta.
  const [workflowEditAllowed, setWorkflowEditAllowed] = useState(false)
  // S368 — ADDENDUM 23/E29 no, esto viene del MERGE: develop endureció este mismo camino en la
  // ventana ANTIGUA (H145, #2946) y la nueva se había quedado sin ello. No es regresión del merge
  // —este código es anterior— pero entregar la ventana que viene a sustituir a la otra con un
  // camino MÁS FRÁGIL que el suyo no se sostiene.
  const [openingWorkflow, setOpeningWorkflow] = useState(false)
  // Balanced waiting for the full order row (X2) that N1 does not carry.
  const [openingBalanced, setOpeningBalanced] = useState(false)
  useEffect(() => {
    if (!open) return undefined
    let alive = true
    apiGet('indexValues/available')
      .then((indexValues) => {
        if (alive) setWorkflowEditAllowed((indexValues || []).some((indexValue) => indexValue.name === 'APPROVED'))
      })
      .catch(() => { if (alive) setWorkflowEditAllowed(false) })
    return () => { alive = false }
  }, [open])
  // RN76/RN30/T2: the General edit batch lives here, not inside GeneralBlock, so it survives the
  // user looking at another block and coming back (S95) — GeneralBlock only reads/writes it.
  const unsavedChanges = useUnsavedChanges()
  const saveGeneralRef = useRef(() => Promise.resolve(true))
  // RN4/CU-W4 (T3): Workflow/Balanced take the SITE of the detail — same Dialog, no second one
  // stacked on top — and give it back on close (S139/S140/S143). `viewMode` is the only state that
  // decides which content this Dialog shows; the identity strip below always renders regardless of
  // `viewMode` (RN9/S147).
  const [viewMode, setViewMode] = useState('detail')
  const [workflowData, setWorkflowData] = useState({})
  // T7 (S271/RN71): the row's own "Workflow" save (historia 80, S54/S44 — guards a new external
  // job with incomplete return timing BEFORE calling Sun, and shows Sun's own 409 message
  // verbatim) moves here with the action itself. `originalWorkflowData` is the diff baseline —
  // kept separate from `workflowData` (which `<Workflow>` mutates via `setInitialWorkflow`) so
  // "no changes to save" and the duplicate-planning-group rejection can still be told apart.
  const [originalWorkflowData, setOriginalWorkflowData] = useState({})
  const savingWorkflowRef = useRef(false)
  const [isSavingWorkflow, setIsSavingWorkflow] = useState(false)
  const [nodes, setNodes, onNodesChange] = useNodesState([])
  const balancedSaveRef = useRef(() => {})

  const header = useDetailBlock(() => fetchHeader(currentId), open)
  // Story 213 v2: the detail is the screen FABRICATION_ORDER_DETAIL of the catalog (S199, B-J2): marked, it is a boundary
  // whatever the row or the header say.
  const { markedScreens } = useVision()
  const sensitiveDetail = sensitive || header.data.sensitive === true || markedScreens.includes('FABRICATION_ORDER_DETAIL')
  // The sheet (`createdNode`, extended columns, `workflow`, `forecast`, `expirationDate`) comes from N1
  // when Sun serves those fields there (`header.data.sheet`): X2 is NOT read on open. With an older
  // Sun, N1 lacks them and X2 (`GET fabricationOrder?fabricationOrder=`, CU-L2 step 1) is read after
  // N1, as before. X2 searches by CODE (see `fetchSheet`), so it waits for N1 to bring the code.
  const headerReady = header.status === 'ready'
  const n1Sheet = headerReady && !!header.data.sheet
  // X2 is the FULL order row. With N1 serving the sheet it is read only when something needs it whole:
  // Balanced (the full workflow graph) and "Copy as" (it sends the whole row). `fullRowWanted` is
  // raised by those two and dropped when the detail jumps to another order.
  const [fullRowWanted, setFullRowWanted] = useState(false)
  const fullRow = useDetailBlock(
    () => fetchSheet(header.data.code),
    headerReady && !!header.data.code && (!n1Sheet || fullRowWanted)
  )
  // What every consumer of the sheet reads. With N1 it is ready as soon as the header is, and its
  // refresh is the header's (plus the full row when it was loaded).
  const sheet = n1Sheet
    ? { status: 'ready', data: header.data.sheet, retry: header.retry, refresh: () => { header.refresh(); fullRow.refresh() } }
    : fullRow
  // N2 alerts feed the Materials nav label (RN7/RN52, T6/C5): fetched alongside the header, always
  // on open, independently of which block is selected (plan §2, N2 note).
  // Con Materiales oculto (addendum 15) la llamada no se hace: ver `HIDDEN_BLOCK_KEYS` arriba.
  const alerts = useDetailBlock(() => fetchAlerts(currentId), open && !isBlockHidden('Materials'))

  const operationsActive = activeBlock === 'Operations' || activeBlock === 'Progress'
  const progressActive = activeBlock === 'Progress'
  // H179 (RN5, C6): sin workflow no hay editor que abrir ni nada que balancear, y no se ofrece. Solo con
  // la fila (X2) ya leída: mientras no llega, la ficha se comporta como hoy.
  const workflowAbsent = sheet.status === 'ready' && !!sheet.data.withoutWorkflow
  // Balanced needs the sheet: while it loads or failed there is nothing to balance yet.
  const offersBalanced = sheet.status === 'ready' && !workflowAbsent

  // N7+N8+N9 (T6): one combined fetch for the whole Materials block — three independent reads
  // that this container treats as a single block slot (same P-1 cache/invalidate convention as
  // every other block), CU-L6/CU-L7/CU-L8.
  //
  // ⚠️ H179: las HIJAS (N7) son su propia ranura, aunque se pinten dentro de Materiales. Cambiar el
  // workflow recarga Materiales siempre y las hijas SOLO si C2 dice que generó alguna (S108,
  // `detailBlocksToReload`); juntas en una lectura, recargar una recargaría la otra.
  const fetchMaterials = (id) => Promise.all([
    fetchMaterialRequirements(id),
    fetchConsumedMaterial(id)
  ]).then(([materialRequirements, consumedMaterial]) => ({ materialRequirements, consumedMaterial }))

  // Every block only activates once the header succeeded (A-L1.3/S41) AND it is the selected one.
  // ⚠️ N3 se pide TAMBIÉN con Progress activo (S431): la fila de Progress lleva las cuatro cifras y el
  // tiempo contra el teórico que vivían en la de Workflow, y hoy solo N3 sirve las rechazadas y el
  // teórico por operación. Regla del PO del 23/09: lo que YA EXISTÍA y se mueve funciona HOY, con lo
  // que el backend sirve ahora — no espera a que Sun lo añada a N10.
  // What the opened operations load (N4, theoretical, quality) is shared by Operations and Progress and
  // dropped whenever either of them is invalidated (order change, state change, new workflow...).
  const expansionCache = useOperationExpansions(currentId)
  const operationsBlock = useDetailBlock(() => fetchOperations(currentId), headerReady && operationsActive, expansionCache.reset)
  const documentsBlock = useDetailBlock(() => fetchDocuments(currentId), headerReady && activeBlock === 'Documents')
  const materialsBlock = useDetailBlock(() => fetchMaterials(currentId), headerReady && activeBlock === 'Materials')
  const childrenBlock = useDetailBlock(() => fetchChildren(currentId), headerReady && activeBlock === 'Materials')
  const progressBlock = useDetailBlock(() => fetchProgress(currentId), headerReady && progressActive, expansionCache.reset)
  const partsBlock = useDetailBlock(noopFetcher, headerReady && activeBlock === 'Parts')

  // Materiales es DOS ranuras (sus lecturas y las hijas): para la navegación es un bloque solo, listo
  // cuando lo están las dos y en error si falla cualquiera.
  const materialsSlot = {
    status: [materialsBlock.status, childrenBlock.status].includes('error')
      ? 'error'
      : (materialsBlock.status === 'ready' && childrenBlock.status === 'ready' ? 'ready' : 'loading'),
    retry: () => { materialsBlock.retry(); childrenBlock.retry() }
  }

  const blocksByKey = {
    Operations: operationsBlock,
    Documents: documentsBlock,
    Materials: materialsSlot,
    Progress: progressBlock,
    Parts: partsBlock
  }

  // H179 (CA8, CA10): tras un C2 correcto, el aviso —también en NO_OP— y la recarga de lo que se deriva
  // de la ruta. El aviso vive AQUÍ: recargar la cabecera desmonta General, y con él cualquier aviso suyo.
  const reloadableBlocks = {
    header,
    // Only the full row: with N1 serving the sheet, the header's own refresh covers it.
    sheet: fullRow,
    Operations: operationsBlock,
    Documents: documentsBlock,
    Materials: materialsBlock,
    Progress: progressBlock,
    Children: childrenBlock
  }
  const handleWorkflowAssigned = (result) => {
    showAlert({ severity: 'success', title: t_('Workflow updated correctly'), message: '' })
    // Header and row are read again in the background: the General block stays mounted. The rest is
    // not on screen (or reloads on its own), so it goes back to idle.
    detailBlocksToReload(result).forEach((key) => {
      if (key === 'header' || key === 'sheet') reloadableBlocks[key].refresh()
      else reloadableBlocks[key].invalidate()
    })
    onOrderChanged()
  }

  const navItems = NAV_ITEMS
    .filter((item) => !isBlockHidden(item.key))
    .filter((item) => !item.permission || isUserAuthorized(item.permission))

  // RN76: any action that gives up the window (close, jump to another order) goes through the
  // guard — with nothing pending it runs right away, otherwise UnsavedChangesPrompt takes over.
  // S37 (parent, from the header) and S269 (a child, from the Materials block) are the SAME
  // action: jump the whole detail window to another order's Operations block.
  const handleNavigateToOrder = (orderId) => {
    unsavedChanges.guard(() => {
      setCurrentId(orderId)
      setActiveBlock('Operations')
      setJustSavedGeneral(false)
      setFullRowWanted(false)
      // Every slot holds the previous order's data (and may have a read in flight): all go back to idle.
      Object.values(reloadableBlocks).forEach((block) => block.invalidate())
      partsBlock.invalidate()
      alerts.invalidate()
    })
  }

  const handleClose = () => unsavedChanges.guard(onClose)

  // ESC and a click on the backdrop only close the detail itself. In the workflow editor or Balanced
  // they would throw away the screen that took the dialog's place, with its unsaved work.
  const handleDialogClose = (event, reason) => {
    if (viewMode !== 'detail' && (reason === 'escapeKeyDown' || reason === 'backdropClick')) return
    handleClose()
  }

  // The prompt can be answered while another block is active, and GeneralBlock is not mounted then:
  // its errors (field, 409, delivery confirmation) have nowhere to show. On a failed save the
  // prompt closes, General opens so those errors can be read there, and an alert says why.
  const [promptSaving, setPromptSaving] = useState(false)
  const handlePromptSave = async () => {
    if (promptSaving) return
    setPromptSaving(true)
    try {
      const succeeded = await saveGeneralRef.current()
      if (succeeded) {
        unsavedChanges.resolveSavedAndContinue()
        return
      }
      unsavedChanges.resolveKeepEditing()
      setActiveBlock('General')
      showAlert({ severity: 'error', title: t_('The changes could not be saved'), message: '' })
    } finally {
      setPromptSaving(false)
    }
  }

  // S139/S141: opening the workflow goes through the same RN76 guard as closing/jumping order —
  // with unsaved General changes, UnsavedChangesPrompt decides first and the workflow never takes
  // the site until the user resolves it.
  const handleOpenWorkflow = () => {
    // S368 (portado de H145, #2946): una carga cada vez. Sin esto, dos invocaciones seguidas dejan
    // dos peticiones en vuelo y gana la que conteste la última.
    if (openingWorkflow) {
      return
    }
    // `takeover`: el workflow TOMA esta ventana, no se apila encima. El aviso lo dice con esas
    // palabras y ofrece «guardar y abrir» como salida buena — nadie abre el workflow para perder
    // lo que acaba de escribir.
    unsavedChanges.guard(() => {
      setOpeningWorkflow(true)
      apiGet(`workflowByFabricationOrder/${currentId}`).then((rawData) => {
        // ⚠️⚠️ ESTA GUARDA NO SOBRA AUNQUE HAYA UN `catch` DEBAJO, y quien la lea dentro de un año
        // va a pensar que sí. El motivo: en 401/502/503/504 `apiF` llama a
        // `notifyServerConnectionLost()` y **RESUELVE `undefined` SIN LANZAR**
        // (`Api_Functions.jsx`). Es decir, el `catch` NO VE ese camino — que es justo el que
        // importa, porque es el del servidor caído.
        //
        // Sin la guarda, `data || {}` convertía esa ausencia en un workflow vacío y
        // `setViewMode('workflow')` abría el editor EN BLANCO en vez de avisar: la ausencia
        // convertida en un valor plausible, que es el defecto que esta historia lleva persiguiendo
        // desde el primer día.
        //
        // Se comprueba el `id` y no el objeto entero porque un workflow legítimo SIN NODOS sí tiene
        // id y tiene que abrirse exactamente como antes.
        if (rawData?.id === undefined || rawData?.id === null) {
          showAlert({
            title: t_('Error loading data'),
            message: t_('Error loading data'),
            severity: 'error'
          })
          return
        }
        const data = clearTempDocumentScopes(rawData)
        setWorkflowData(data || {})
        setOriginalWorkflowData(structuredClone(data || {}))
        // Defecto (verificación del hub): `<Workflow>` construye sus PROPIOS nodos de React Flow a
        // partir de `initialWorkflow`. Pasarle aquí los nodos de dominio de Sun —que no llevan
        // `position`— reventaba React Flow en `createNodeInternals` ("Cannot read properties of
        // undefined (reading 'x')") y el workflow no llegaba a pintarse. La ventana original de
        // FabricationOrder.jsx arrancaba con la lista vacía, exactamente igual.
        setNodes([])
        setViewMode('workflow')
      }).catch((error) => {
        // La otra mitad: un rechazo de verdad. Sin esto la promesa quedaba SIN MANEJAR y el usuario
        // se quedaba mirando un menú que no hace nada.
        showAlert({
          title: t_('Error loading data'),
          message: error?.message ? t_(error.message) : t_('Error loading data'),
          severity: 'error'
        })
      }).finally(() => {
        setOpeningWorkflow(false)
      })
    }, 'takeover')
  }

  // Historia 80 (S54/S44): mirrors the row's own `saveWorkflow` — a direct save that never goes
  // through `Workflow.jsx`'s own internal save dialog (hidden here by `hideButtonsSubWorkflow`
  // once editing starts). S54 blocks BEFORE calling Sun when a job created in this session is
  // external with no return timing; S44 surfaces Sun's own 409 message verbatim otherwise.
  // Saving a large workflow takes seconds; a second Save meanwhile sent a concurrent PUT that Sun
  // rejected ("Error saving workflow") after the first had already saved. Same pair as Workflow.jsx:
  // the ref is the synchronous guard (a click before repaint already sees it), the state disables Save.
  const saveWorkflow = async () => {
    if (savingWorkflowRef.current) {
      return
    }
    savingWorkflowRef.current = true
    setIsSavingWorkflow(true)
    try {
      await persistWorkflow()
    } catch (error) {
      showAlert(alertForSaveError(error, t_))
    } finally {
      savingWorkflowRef.current = false
      setIsSavingWorkflow(false)
    }
  }

  const persistWorkflow = async () => {
    if (findNewExternalJobMissingReturnTiming(workflowData?.nodes)) {
      showAlert({
        title: t_('An external job must define its return timing'),
        severity: 'warning',
        message: ''
      })
      return
    }

    const diff = diffObjects(originalWorkflowData, workflowData, ['workflowPositions'])
    const existsPendingDocuments = await apiPut('document/checkTemp', collectTempDocumentUUIDs(workflowData))
    if (diff.equals && !existsPendingDocuments.value) {
      showAlert({ title: t_('No changes to save'), severity: 'info', message: '' })
      return
    }

    // Story 183 (C1, RN8.1): the third user-session save route — the night mark goes explicit on every
    // job, or Sun would keep the persisted one.
    // Story 186 (S114): same feature group payload preparation as the editor of the templates.
    const workflowToSave = withExplicitNightJob(await prepareFabricationOrderWorkflowForSave(
      { ...workflowData, workflowPositions: updatePositions(nodes, workflowData) },
      originalWorkflowData
    ))
    try {
      await apiPut('workflow/false/false/' + workflowToSave.indexValue.name + '/' + workflowToSave.partReferenceId, workflowToSave)
      showAlert({ severity: 'success', title: t_('Workflow updated correctly'), message: '' })
      onOrderChanged()
      handleCloseWorkflow()
    } catch (error) {
      if (isDuplicatePlanningGroupSaveError(error)) {
        showAlert({
          severity: 'error',
          message: t_(DUPLICATE_PLANNING_GROUP_MESSAGE_KEY, { values: extractDuplicatePlanningGroupValues(error).join(', ') })
        })
        return
      }
      showAlert(alertForSaveError(error, t_))
    }
  }

  // S140/P-1: always back to the SAME order's Operations block, and that block is reread — never
  // served from the pre-workflow cache (RN4/CA4.6).
  const handleCloseWorkflow = () => {
    setViewMode('detail')
    setActiveBlock('Operations')
    operationsBlock.invalidate()
  }

  // S143: Balanced takes the site the same way; closing it does NOT force a block — whichever block
  // was active before opening Balanced is still the one showing (it was never changed).
  const openBalancedWith = (row) => {
    // ⚠ Fix (verificación del hub, 2026-09-14): Balanced se precarga con el workflow DE LA FILA
    // (X2), no con `workflowByFabricationOrder`, y necesita `partReference`. La fila lo trae a su
    // nivel y `workflow.partReference` viene a null, así que se completa igual que hacía la
    // ventana original de FabricationOrder.jsx antes de T7 ("Ensure partReference (exposed at row
    // level) is present for SET_WORKFLOW"): sin él, `loadWorkflowIntoBalanced` revienta al
    // construir el `SET_WORKFLOW` y la pantalla se queda en "Loading data" para siempre.
    const rowWorkflow = row.workflow || {}
    setWorkflowData({
      ...rowWorkflow,
      partReference: rowWorkflow.partReference ?? row.partReference ?? {}
    })
    setViewMode('balanced')
  }

  const handleOpenBalanced = () => {
    if (openingBalanced) return
    unsavedChanges.guard(() => {
      // Sun without the sheet in N1: the sheet IS the full row, already read.
      if (!n1Sheet) {
        openBalancedWith(sheet.data)
        return
      }
      // N1 carries a summary of the workflow, not its graph: the full row is read now, once, and
      // Balanced opens when it lands (see the effect below).
      if (fullRow.status === 'ready') {
        openBalancedWith(fullRow.data)
        return
      }
      if (fullRow.status === 'error') fullRow.retry()
      setFullRowWanted(true)
      setOpeningBalanced(true)
    }, 'takeover')
  }

  useEffect(() => {
    if (!openingBalanced) return
    if (fullRow.status === 'ready') {
      setOpeningBalanced(false)
      openBalancedWith(fullRow.data)
    } else if (fullRow.status === 'error') {
      setOpeningBalanced(false)
      showAlert({ severity: 'error', title: t_('Error loading data'), message: t_('Error loading data') })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openingBalanced, fullRow.status])

  const handleCloseBalanced = () => setViewMode('detail')

  // Balanced rewrote the workflow: Operations and the row (workflow data) are stale.
  const handleBalancedSaved = () => {
    handleCloseBalanced()
    if (operationsActive) operationsBlock.refresh()
    else operationsBlock.invalidate()
    sheet.refresh()
    onOrderChanged()
  }

  useEffect(() => {
    if (!justSavedGeneral) return undefined
    const timer = setTimeout(() => setJustSavedGeneral(false), 4000)
    return () => clearTimeout(timer)
  }, [justSavedGeneral])

  const handleNotFound = () => setNotFound(true)

  useEffect(() => {
    if (header.status === 'error' && header.error?.status === 404) {
      setNotFound(true)
    }
  }, [header.status, header.error])

  // A background re-read that fails keeps the data on screen (`refreshError`): a 404 still means the
  // order is gone, any other failure is only told.
  const handleRefreshError = (error) => {
    if (Object.keys(error).length === 0 && !(error instanceof Error)) return
    if (error.status === 404) setNotFound(true)
    else showAlert({ severity: 'warning', title: t_('Could not load the order'), message: '' })
  }
  useEffect(() => { handleRefreshError(header.refreshError) }, [header.refreshError]) // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { handleRefreshError(fullRow.refreshError) }, [fullRow.refreshError]) // eslint-disable-line react-hooks/exhaustive-deps

  // E14/S66: whether it is the header read, a background re-read or an action (status/pause/resume)
  // that answers 404 because the order was deleted mid-session, the outcome is the same one path —
  // inform and close (RN31/RN78), never a bare error box that leaves the user stuck on a ghost order.
  // The notice goes up with the close: this dialog unmounts in the same tick, so the list shows it.
  useEffect(() => {
    if (notFound) {
      onOrderChanged()
      onClose({ notice: { severity: 'error', title: t_('This order no longer exists'), message: '' } })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [notFound])

  // Closing only closes: a cancel changes nothing, and an increase is still creating parts in the
  // background when the form closes — reloading the header THEN showed the old quantity and, by
  // unmounting this dialog (it lives under `headerReady`), stopped the progress polling too.
  const handleModifyQuantityClose = () => setModifyQuantityOpen(false)

  // Status change and pause/resume: N1/X2 re-read in the background, so the content no longer
  // remounts — the blocks whose rows those actions change (part and operation states, progress)
  // are invalidated explicitly instead. Materials and Documents are not touched by either action.
  // The block on screen is refreshed in the background (it keeps its filters and search); the others
  // go back to idle.
  const handleOrderStateChanged = () => {
    header.refresh()
    fullRow.refresh()
    partsBlock.invalidate()
    if (progressActive) progressBlock.refresh()
    else progressBlock.invalidate()
    if (operationsActive) operationsBlock.refresh()
    else operationsBlock.invalidate()
    onOrderChanged()
  }

  // The quantity really changed: everything that shows it or depends on the order's stamp reloads.
  const handleQuantityChanged = () => {
    header.refresh()
    fullRow.refresh()
    materialsBlock.invalidate()
    childrenBlock.invalidate()
    partsBlock.invalidate()
    progressBlock.invalidate()
    operationsBlock.invalidate()
    onOrderChanged()
  }

  // Same object while the order data is the same: a new one on every render reset the form of the
  // Modify quantity dialog (its effect depends on it) each time this dialog rendered.
  const handleRetryWorkflow = () => operationsBlock.retry()

  const quantityOrder = useMemo(
    () => ({ id: currentId, quantity: header.data.quantity?.nominal, fabricationOrder: header.data.code }),
    [currentId, header.data.quantity?.nominal, header.data.code]
  )

  const closeButton = (
    <IconButton aria-label={t_('Close')} title={t_('Close')} onClick={handleClose}>
      <CloseRounded fontSize='small' />
    </IconButton>
  )

  return (
    <Dialog
      open={open}
      onClose={handleDialogClose}
      aria-labelledby='fo-detail-code'
      fullWidth
      maxWidth='xl'
      data-testid='fo-detail-dialog'
      PaperProps={{
        sx: {
          width: '100%',
          // ⚠️⚠️ AQUÍ ESTÁ EL LEVER DEL ANCHO. No en el `minWidth` de más abajo, que es inerte —ver
          // el comentario largo allí, que documenta el error de haberlo tocado creyendo lo contrario.
          //
          // `max(1320px, 90vw)` = SUELO MÁS PORCENTAJE, y el suelo es el número que ya existía:
          //   · el PO quiere ~90% del viewport porque en su monitor de 1920 el diálogo ocupaba el
          //     69% (1320 de 1920), con 300 px de margen a cada lado;
          //   · pero `90vw` A SECAS ENCOGERÍA las pantallas pequeñas, porque 90vw siempre es menor
          //     que el hueco disponible (100vw − 16): a 1280 bajaría de 1264 a 1152 y a 880 de 864
          //     a 792, y 1280 es justo el ancho donde la tabla ya iba apretada.
          //   · con el `max()`, por debajo de ~1467 de viewport manda el suelo de 1320, que a su
          //     vez queda por encima del hueco disponible, así que ahí gana `width: 100%` y NO SE
          //     TOCA NADA. Por encima, manda el 90%.
          //
          // ⚠️⚠️ MEDIDO EN NAVEGADOR, y desmintió un número que dábamos por bueno:
          //
          //     viewport   1920   1512   1280
          //     Paper      1728   1361   1280     <- medido, no calculado
          //     contenedor 1419   1052    971
          //     la tabla   1417   1267   1267
          //     se desplaza  NO    SÍ     SÍ      (217 px y 298 px)
          //
          // EL SUELO DE LA TABLA NO ES 788, ES 1267. Los 788 eran la suma de los `minWidth`
          // DECLARADOS, y en pantalla las columnas se pintan MUY por encima de su mínimo. Los dos
          // lados de esta historia razonamos contra un número que el navegador nunca usó — y el
          // `minWidth` volvió a ser el sitio donde se buscó una respuesta que no estaba.
          //
          // 📌 Y ESO HACE ESTE CAMBIO MÁS NECESARIO DE LO QUE PARECÍA, no menos: con el techo de
          // 1320 el contenedor daba ~1026 para una tabla que pide 1267, así que SE DESPLAZABA
          // TAMBIÉN A 1920. Ahora no. El umbral medido es ~1737 px de viewport.
          //
          // 📌 A 1512 y a 1280 SIGUE desplazándose, y NO lo arregla el ancho: a 1280 el Paper ya
          // ocupa el 100% del viewport (1280 medidos, no los 1264 que yo calculaba — el `m: 1` no
          // llega a aplicarse ahí). No queda ancho que dar; lo que sobra son columnas. Hueco
          // conocido y declarado, no un efecto de este cambio.
          //
          // ⚠️ `maxWidth` SOLO PONE TECHO: nunca fuerza ancho, así que no puede reintroducir el
          // defecto del addendum 23 B (contenido inalcanzable en ventana estrecha).
          maxWidth: 'max(1320px, 90vw)',
          // S358 — ADDENDUM 23 (E29) B: el ancho de diseño es 1120, pero NO por debajo del hueco que
          // hay. Esto decía `minWidth: { xs: 'auto', sm: 1120 }`, y el breakpoint `sm` de MUI son
          // 600px: cualquier ventana de 600 para arriba exigía 1120 de diálogo.
          //
          // ⚠ Lo grave no era que se viera estrecho, es que el contenido quedaba INALCANZABLE.
          // Medido con la ventana a 880: el diálogo en `izq: -120, der: 1000` —fuera por los dos
          // lados— y `document.scrollWidth === clientWidth`, o sea que la página NO desplaza. Se leía
          // "RDEN DE FABRICACIÓN", "ECUCIÓN", "ECHAZADA". No hay forma de llegar a lo que sobresale.
          // Y no es un caso de laboratorio: con el zoom al 125% en una pantalla de 1366 el viewport
          // efectivo baja de 1120.
          //
          // `min()` y no un breakpoint: los breakpoints son escalones y esto es continuo — entre 600
          // y 1120 el defecto crece píxel a píxel. Con `100%` de por medio, el diálogo pide 1120
          // cuando caben y el hueco disponible cuando no. En pantalla ancha manda 1120 igual que
          // antes, así que el diseño no se toca donde hoy se ve bien.
          // ⚠️⚠️ CORRECCIÓN DE UN ERROR MÍO, Y LA ESCRIBO ENTERA PORQUE EL SIGUIENTE LA NECESITA:
          // en el addendum 35 subí este valor de 1120 a 1320 creyendo que ensanchaba el diálogo, y
          // NO CAMBIÓ UN PÍXEL EN NINGUNA PANTALLA. Toqué el número que no manda.
          //
          // CÓMO SE RESUELVE EL ANCHO DE VERDAD, que es lo que yo no miré:
          //     width: '100%'            -> 100% del contenedor flex, que encoge por `m: 1`
          //     maxWidth: 1320           -> EL TOPE. El único que manda.
          //     minWidth: min(X, 100%)   -> NUNCA puede exceder el 100%, así que SIEMPRE se
          //                                 satisface: es INERTE, y lo es A PROPÓSITO.
          // Esa inercia es justo lo que arregló el addendum 23 B: antes decía `minWidth: 1120` a
          // secas, que SÍ ataba y forzaba 1120 sobre un hueco de 864 — el contenido inalcanzable.
          // Envolverlo en `min(..., 100%)` lo desactivó. Subir un valor desactivado no hace nada.
          //
          // ⚠️ Y LA ARITMÉTICA QUE DEJÉ AQUÍ ERA FALSA EN SU PREMISA: decía "a 1280 el diálogo mide
          // 1120 y la tabla recibe 826". No. Con `width: '100%'` el diálogo YA medía 1264 a 1280 y
          // la tabla YA recibía 970. El 826 salía de suponer que `minWidth` fijaba el ancho.
          // Se leía como una medición y era una suposición con formato de medición — que es
          // exactamente la clase de evidencia que esta historia lleva entera cazando.
          //
          // LOS NÚMEROS BUENOS (ancho del diálogo = min(viewport − 16, maxWidth)):
          //     viewport   1920   1512   1280    880
          //     ancho      1320   1320   1264    864
          //     la tabla   1026   1026    970    744   (nav 246 si viewport >= 1200, si no 72;
          //                                            padding 48; suelo de la tabla 788)
          // O sea: a 880 YA HAY desplazamiento horizontal (744 < 788), y no lo introdujo nadie hoy.
          //
          // 📌 PENDIENTE: el PO quiere ~90% del viewport para aprovechar su monitor de 1920, donde
          // hoy el diálogo ocupa el 69%. El lever para eso es `maxWidth`, NO esto. Y `90vw` a secas
          // ENCOGERÍA a 1280 (1152) y a 880 (792), porque 90vw siempre es menor que el hueco
          // disponible. Propuesto `max(1320px, 90vw)`, que da 1728 a 1920 y no toca nada por debajo
          // de 1320 de viewport. A la espera de confirmación.
          //
          // Esta línea se queda en 1320 porque da igual el valor mientras esté envuelta en `min()`:
          // lo que importa es que NO ate. Si algún día alguien quiere que ate, que sepa que eso es
          // reintroducir el defecto del addendum 23 B.
          minWidth: { xs: 'auto', sm: 'min(1320px, 100%)' },
          // Mejora 9 (pasada 33). ⚠ Aritmética antes que cambio: 671 de 729 **ya es el 92%**, así que
          // subir el porcentaje no habría dado un solo píxel — el `maxHeight: '92vh'` era justo el
          // que producía esos 671. Lo que sobra es el MARGEN del diálogo, 32px por lado por defecto
          // en MUI. Bajándolo a 8 y dejando que el tope sea el alto realmente disponible, el mismo
          // hueco de 729 da ~713 en vez de 671. En pantallas altas sigue mandando el alto de diseño
          // (840), que es lo que fija el mockup.
          height: 840,
          maxHeight: 'calc(100% - 16px)',
          m: 1,
          borderRadius: 3,
          overflow: 'hidden',
          backgroundColor: groundColor(theme)
        }
      }}
    >
      <DialogContent sx={{ p: 0, display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
        <SensitiveBoundary sensitive={sensitiveDetail}>
          <Box sx={{ position: 'absolute', top: 8, right: 8, zIndex: 2, maxWidth: 'calc(100% - 16px)' }}>{AlertElement}</Box>
          {/* Same blocking "Saving" overlay as the fabrication order list's workflow window: saving a large
            workflow takes seconds, and a second Save meanwhile sent a concurrent, rejected save. */}
          <SmartDialog
            setOpen={isSavingWorkflow}
            accept={false}
            cancel={false}
            close={false}
            title={createDialogTitle({ color: 'info', title: t_('Saving'), icon: HourglassTopOutlined })}
            renderComponent={
            isSavingWorkflow
              ? (
                <Stack data-testid='fo-workflow-saving' spacing={2} alignItems='center' sx={{ padding: '1.5em 2.5em' }}>
                  <CircularProgress />
                </Stack>
                )
              : ''
          }
          />
          {!headerReady && header.status === 'error' && header.error?.status !== 404 && (
            <Box data-testid='header-error' sx={{ p: 3, display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
              <Box>
                <Typography variant='body1'>{t_('Could not load the order')}</Typography>
                <Button onClick={() => header.retry()}>{t_('Retry')}</Button>
              </Box>
              {closeButton}
            </Box>
          )}

          {/* RN10: the header paints with its own read and SAYS SO while it loads. Without this
            branch a slow (or hung) backend rendered an EMPTY modal: the content below is gated on
            `headerReady`, the branch above only covers errors, and a 404 closes the dialog. Found
            by opening the real app while Sun was unresponsive. */}
          {!headerReady && header.status !== 'error' && (
            <Box data-testid='header-loading' sx={{ p: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
              <CircularProgress size={20} />
              <Typography variant='body2' color='text.secondary' sx={{ flexGrow: 1 }}>{t_('Loading...')}</Typography>
              {closeButton}
            </Box>
          )}

          {/* S368 (portado de H145): el hueco mientras se pide el workflow, ANTES de que la pantalla
            tome el sitio. Usa el mismo `TakeoverFallback` que los dos módulos perezosos, porque
            para quien mira es la misma espera: el cuerpo del diálogo ocupado mientras llega algo
            que va a ocuparlo entero. */}
          {headerReady && viewMode === 'detail' && (openingWorkflow || openingBalanced) && <TakeoverFallback />}

          {headerReady && viewMode === 'detail' && !openingWorkflow && !openingBalanced && (
            <>
              {/* RN17/S30: the header never scrolls with the block — it is a sibling of the
                nav+content row below, never its ancestor nor its descendant. */}
              <DetailHeader
                header={header.data}
                sheet={sheet.status === 'ready' ? sheet.data : {}}
                lazyCopy={n1Sheet}
                copySheet={fullRow.status === 'ready' ? fullRow.data : {}}
                copyStatus={fullRow.status}
                onCopyRequested={() => {
                  if (fullRow.status === 'error') fullRow.retry()
                  setFullRowWanted(true)
                }}
                fabricationOrderId={currentId}
                onNavigateToParent={handleNavigateToOrder}
                onOpenModifyQuantity={() => setModifyQuantityOpen(true)}
                onOpenBalanced={handleOpenBalanced}
                offersBalanced={offersBalanced}
                onAfterStatusChange={handleOrderStateChanged}
                onAfterPauseResume={handleOrderStateChanged}
                onNotFound={handleNotFound}
                onClose={handleClose}
                showAlert={showAlert}
              />

              <Box sx={{ display: 'flex', flex: 1, minHeight: 0 }}>
                <Box
                  data-testid='detail-nav'
                  sx={{
                    width: { xs: 72, lg: 246 },
                    flexShrink: 0,
                    borderRight: `1px solid ${alpha(theme.palette.text.primary, 0.08)}`,
                    backgroundColor: raisedColor(theme),
                    py: 2,
                    px: { xs: 1, lg: 2 },
                    overflowY: 'auto'
                  }}
                >
                  <Typography
                    variant='caption'
                    sx={{
                      display: { xs: 'none', lg: 'block' },
                      color: 'text.secondary',
                      fontWeight: 700,
                      letterSpacing: 1,
                      textTransform: 'uppercase',
                      px: 1,
                      mb: 1
                    }}
                  >
                    {t_('Blocks')}
                  </Typography>
                  <Stack spacing={0.5}>
                    {navItems.map((item) => {
                      const NavIcon = NAV_ICON_BY_KEY[item.key] || DescriptionOutlinedIcon
                      const isActive = activeBlock === item.key
                      return (
                        <Button
                          key={item.key}
                          onClick={() => setActiveBlock(item.key)}
                          data-active={isActive ? 'true' : 'false'}
                          aria-current={isActive ? 'page' : undefined}
                          title={t_(item.labelKey || item.key)}
                          startIcon={<NavIcon fontSize='small' sx={{ color: isActive ? 'primary.main' : 'text.secondary' }} />}
                          sx={{
                            justifyContent: { xs: 'center', lg: 'flex-start' },
                            textTransform: 'none',
                            borderRadius: 2,
                            minWidth: 0,
                            px: { xs: 1, lg: 1.5 },
                            py: 1,
                            color: isActive ? 'primary.main' : 'text.primary',
                            fontWeight: isActive ? 800 : 500,
                            backgroundColor: isActive ? alpha(theme.palette.primary.main, 0.14) : 'transparent',
                            '&:hover': {
                              backgroundColor: isActive ? alpha(theme.palette.primary.main, 0.2) : alpha(theme.palette.text.primary, 0.06)
                            }
                          }}
                        >
                          <Box component='span' sx={navLabelSx}>{t_(item.labelKey || item.key)}</Box>
                          {/* RN7/RN52 (T6): Materials is the ONLY block label with an alert — a dot, never
                            a count (RN7 forbids counters on block labels). */}
                          {item.key === 'Materials' && !!alerts.data.materials && (
                            <Box component='span' data-testid='materials-nav-alert' sx={{ ml: 'auto', display: 'inline-flex' }}>
                              <WarningAmberRounded fontSize='small' sx={{ color: 'warning.main' }} />
                            </Box>
                          )}
                        </Button>
                      )
                    })}
                  </Stack>
                </Box>

                <DetailErrorBoundary
                  key={`${activeBlock}-${currentId}`}
                  sx={{ flex: 1, minWidth: 0, overflowY: 'auto', backgroundColor: groundColor(theme), p: { xs: 2, md: 3 } }}
                >
                  {activeBlock === 'General' && (
                    <GeneralBlock
                      header={header.data}
                      sheet={sheet.data}
                      sheetReady={sheet.status === 'ready'}
                      sheetError={sheet.status === 'error'}
                      onRetrySheet={() => sheet.retry()}
                      fabricationOrderId={currentId}
                      unsavedChanges={unsavedChanges}
                      onOpenModifyQuantity={() => setModifyQuantityOpen(true)}
                      onSaved={() => {
                      // Background re-read (N1 and X2 in parallel, the code does not change on
                      // N16): the block stays mounted and keeps showing what was just saved.
                        header.refresh()
                        fullRow.refresh()
                        setJustSavedGeneral(true)
                        onOrderChanged()
                      }}
                      registerSave={(fn) => { saveGeneralRef.current = fn }}
                      onWorkflowAssigned={handleWorkflowAssigned}
                      showAlert={showAlert}
                    />
                  )}
                  {activeBlock !== 'General' && (
                    <>
                      {blocksByKey[activeBlock].status === 'loading' && <Typography>{t_('Loading...')}</Typography>}
                      {blocksByKey[activeBlock].status === 'error' && (
                        <Box data-testid='block-error'>
                          <Typography>{t_('Could not load this block')}</Typography>
                          <Button onClick={() => blocksByKey[activeBlock].retry()}>{t_('Retry')}</Button>
                        </Box>
                      )}
                      {blocksByKey[activeBlock].status === 'ready' && activeBlock === 'Operations' && (
                        <OperationsBlock
                          fabricationOrderId={currentId}
                          operations={operationsBlock.data}
                          onOpenWorkflow={handleOpenWorkflow}
                          offersOpenWorkflow={!workflowAbsent}
                          workflowUsed={!!sheet.data.workflow?.used}
                        // H187 (RN17): the same text as the table, from the single formatter; never `workflow.name` (D-J1).
                          workflowName={describeWorkflow(sheet.data.workflow, t_).text}
                          balancedType={sheet.data.workflow?.balancedType || ''}
                          expansionCache={expansionCache}
                          onPlanningGroupSaved={() => {
                            // Operations keeps showing the saved group; Progress re-reads on its next opening.
                            operationsBlock.refresh()
                            progressBlock.invalidate()
                          }}
                        />
                      )}
                      {blocksByKey[activeBlock].status === 'ready' && activeBlock === 'Progress' && (
                        <ProgressBlock
                          fabricationOrderId={currentId}
                          progress={progressBlock.data}
                          workflowOperations={operationsBlock.status === 'ready' ? operationsBlock.data : []}
                          workflowStatus={operationsBlock.status}
                          onRetryWorkflow={handleRetryWorkflow}
                          statusProgress={header.data.statusProgress}
                          expansionCache={expansionCache}
                        />
                      )}
                      {blocksByKey[activeBlock].status === 'ready' && activeBlock === 'Documents' && (
                        <DocumentsBlock
                          fabricationOrderId={currentId}
                          fabricationOrderCode={header.data.code}
                          documents={documentsBlock.data}
                          onDocumentsChanged={() => documentsBlock.refresh()}
                          showAlert={showAlert}
                        />
                      )}
                      {blocksByKey[activeBlock].status === 'ready' && activeBlock === 'Parts' && (
                        <PartsBlock fabricationOrderId={currentId} onNotFound={handleNotFound} />
                      )}
                      {blocksByKey[activeBlock].status === 'ready' && activeBlock === 'Materials' && (
                        <MaterialsBlock
                          fabricationOrderId={currentId}
                          header={header.data}
                          childOrders={Array.isArray(childrenBlock.data) ? childrenBlock.data : []}
                          materialRequirements={materialsBlock.data.materialRequirements}
                          consumedMaterial={materialsBlock.data.consumedMaterial}
                          onNavigateToOrder={handleNavigateToOrder}
                        />
                      )}
                    </>
                  )}
                </DetailErrorBoundary>
              </Box>

            </>
          )}

          {/* Fuera del gate `headerReady`: guardar ya no lo tira (relee en segundo plano), pero si la
            relectura de N1 fallara el gate caería y no debe llevarse la confirmación con él. */}
          {justSavedGeneral && viewMode === 'detail' && (
            <Stack
              direction='row'
              spacing={1.5}
              alignItems='center'
              data-testid='changes-saved-bar'
              sx={{
                px: 3,
                py: 2,
                borderTop: `2px solid ${theme.palette.success.main}`,
                backgroundColor: raisedColor(theme),
                backgroundImage: `linear-gradient(${alpha(theme.palette.success.main, 0.12)}, ${alpha(theme.palette.success.main, 0.12)})`
              }}
            >
              <CheckCircleIcon fontSize='small' sx={{ color: 'success.main' }} />
              <Typography variant='body2' sx={{ fontWeight: 700 }}>{t_('Changes saved')}</Typography>
            </Stack>
          )}

          {headerReady && viewMode === 'workflow' && (
            <Box data-testid='workflow-view' sx={{ flex: 1, minHeight: 0, overflow: 'auto', p: 3 }}>
              <Typography id='fo-detail-code' data-testid='workflow-view-identity'>{header.data.code}</Typography>
              <Button data-testid='workflow-view-close' onClick={handleCloseWorkflow}>{t_('Close')}</Button>
              {isUserAuthorized('WORKFLOWS_TAB') && (
                <Button data-testid='workflow-view-save' onClick={saveWorkflow} disabled={isSavingWorkflow}>
                  {isSavingWorkflow ? t_('Saving...') : t_('Save')}
                </Button>
              )}
              <Suspense fallback={<TakeoverFallback />}>
                <WorkflowHierarchyProvider>
                  {/* `startInEditMode` es lo que T7 perdió al portar la ventana: sin él el workflow se
                  monta en modo lectura —13 de 13 campos de solo lectura y 16 de 25 botones
                  deshabilitados, el "todo en gris" que reportó el PO— y hay que descubrir el botón
                  EDIT. La ventana original lo pasaba (`startInEditMode={allowEdit}`). */}
                  <Workflow
                    frontendConfig={frontendConfig}
                    initialWorkflow={workflowData}
                    setInitialWorkflow={setWorkflowData}
                    hideButtonsSubWorkflow
                    nodes={nodes}
                    setNodes={setNodes}
                    onNodesChange={onNodesChange}
                    fromFO
                    startInEditMode={workflowEditAllowed}
                    allowEdit={workflowEditAllowed}
                    fabricationOrderOperable={isFabricationOrderOperable(header.data.status)}
                  />
                </WorkflowHierarchyProvider>
              </Suspense>
            </Box>
          )}

          {headerReady && viewMode === 'balanced' && (
            <Box data-testid='balanced-view' sx={{ flex: 1, minHeight: 0, overflow: 'auto', p: 3 }}>
              <Typography id='fo-detail-code' data-testid='balanced-view-identity'>{header.data.code}</Typography>
              <Button data-testid='balanced-view-close' onClick={handleCloseBalanced}>{t_('Close')}</Button>
              {isUserAuthorized('WORKFLOWS_TAB') && (
                <Button data-testid='balanced-view-save' onClick={() => balancedSaveRef.current()}>{t_('Save')}</Button>
              )}
              <Suspense fallback={<TakeoverFallback />}>
                <Balanced
                  preloadWorkflow={workflowData}
                  startInEditMode
                  hideHeader
                  saveRef={balancedSaveRef}
                  onSaved={handleBalancedSaved}
                />
              </Suspense>
            </Box>
          )}

          {headerReady && (
            <>
              <ModifyQuantityDialog
                fabricationOrder={quantityOrder}
                open={modifyQuantityOpen}
                onClose={handleModifyQuantityClose}
                onCompleted={handleQuantityChanged}
                showAlert={showAlert}
              />

              {/* The field names are derived HERE and not in GeneralBlock because the block can be
                unmounted when the prompt shows (RN30/S95). The extended column names come from the
                container's state, which GeneralBlock fills. */}
              <UnsavedChangesPrompt
                open={unsavedChanges.isPromptOpen}
                reason={unsavedChanges.pendingReason}
                fields={changedFieldLabels(unsavedChanges.pending, unsavedChanges.extendedColumns).map((change) => (change.labelKey ? t_(change.labelKey) : change.label))}
                saving={promptSaving}
                onSave={handlePromptSave}
                onDiscard={() => unsavedChanges.resolveDiscardAndContinue()}
                onKeepEditing={() => unsavedChanges.resolveKeepEditing()}
              />
            </>
          )}
        </SensitiveBoundary>
      </DialogContent>
    </Dialog>
  )
}

export default FabricationOrderDetailDialog
